import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Chip, FormControl, Grid, InputLabel, LinearProgress, Link, MenuItem, Select, TextField, Typography } from "@mui/material";
import { useUsefulLinksController } from "./context/useful-links-controller";
import { useUsefulLinksState } from "./context/useful-links-context";
import { IListUsefulLinksRequest } from "../../model/base/request/list-useful-links-request";
import PageHeader from "../components/shared/page-header";
import ClearIcon from '@mui/icons-material/Clear';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Pagination } from "@mui/material";
import FilterPanel, { ResultsPanel } from '../components/shared/filter-panel';
import EmptyState, { hasActiveFilters } from '../components/shared/empty-state';
import ListSkeleton from '../components/shared/list-skeleton';

export function UsefulLinksComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData, loadLinkCategories } = useUsefulLinksController();
    const { usefulLinks, linkCategories } = useUsefulLinksState();
    const [paramsRequest, setParamsRequest] = useState<IListUsefulLinksRequest>({ page: 0, pageSize: 10 } as IListUsefulLinksRequest);

    useEffect(() => {
        const loadInitialData = async () => {
            await loadLinkCategories();
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequest]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListUsefulLinksRequest);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setParamsRequest({ ...paramsRequest, page: value - 1 });
    };

    return (
        <Box>
            <PageHeader
                title="Links Úteis"
                subtitle="Acesse links externos relacionados ao município, incluindo sites de órgãos públicos, serviços online e recursos úteis para o cidadão."
                breadcrumbs={[{ label: 'Links Úteis' }]}
            />

            {initialLoading ? (
                <ListSkeleton />
            ) : (
                <React.Fragment>
                    <FilterPanel>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={7}>
                                <TextField
                                    fullWidth
                                    label="Buscar por nome"
                                    placeholder="Digite para buscar pelo nome do link"
                                    value={paramsRequest.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequest, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel>Categoria</InputLabel>
                                    <Select
                                        value={paramsRequest.categoryId || ''}
                                        label="Categoria"
                                        onChange={(e) => setParamsRequest({ ...paramsRequest, page: 0, categoryId: e.target.value as number })}
                                    >
                                        <MenuItem value="">
                                            <em>Todas</em>
                                        </MenuItem>
                                        {linkCategories.map((category) => (
                                            <MenuItem key={category.id} value={category.id}>
                                                {category.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </FilterPanel>

                    <ResultsPanel>
                        {loadMoreData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <LinearProgress sx={{ width: '50%' }} />
                            </Box>
                        ) : usefulLinks.items.length === 0 ? (
                            <EmptyState title="Nenhum link encontrado" filtered={hasActiveFilters(paramsRequest)} onClearFilters={handleClearFilters} />
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {usefulLinks.items.map((link) => (
                                        <Card
                                            key={link.id}

                                        >
                                            <CardContent>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                                    <Box sx={{ flex: 1, minWidth: 200 }}>
                                                        <Typography variant="h6" component="div" gutterBottom>
                                                            {link.name}
                                                        </Typography>
                                                        {link.category && (
                                                            <Chip
                                                                label={link.category}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    </Box>
                                                    <Button
                                                        variant="contained"
                                                        endIcon={<OpenInNewIcon />}
                                                        component={Link}
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ textDecoration: 'none' }}
                                                    >
                                                        Acessar
                                                    </Button>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Box>

                                {usefulLinks.totalRecords && usefulLinks.totalRecords > usefulLinks.pageSize && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                        <Pagination
                                            count={Math.ceil(usefulLinks.totalRecords / usefulLinks.pageSize)}
                                            page={(paramsRequest.page || 0) + 1}
                                            onChange={handlePageChange}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            siblingCount={1}
                                            boundaryCount={1}
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    </ResultsPanel>
                </React.Fragment>
            )}
        </Box>
    );
}
